/**
 * LaunchWizard.tsx
 *
 * Reusable full-page launcher used by both EPR Step 1 and 360 Step 2
 * (and any future launcher that fits the same shape).
 *
 * Pattern matches the legacy "Start a Review" screen the user
 * flagged as the right tone:
 *   - Eyebrow + title + friendly intro paragraph
 *   - Top right "← Back" link
 *   - 3 horizontal step tabs (Set up | … | Sent), active one highlighted
 *   - Card per active step
 *   - Footer with Back / Next or Submit
 *   - Final "sent" step takes a success-view ReactNode (links list, etc.)
 *
 * INVARIANT: this component does NOT own the form state. Each launcher
 * page holds its own state and passes per-step content + per-step
 * validity. Keeps the wizard reusable; per-launcher quirks stay where
 * they belong.
 *
 * ENGINEERING.md compliance:
 *   §0 — class fix: launch UX is now reusable, not copy-pasted
 *   §1 — PascalCase component, prop names verbose-and-clear
 *   §3c — props are a config object (4 fields), well under the 6 ceiling
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import ConfirmDialog, { type ConfirmAction, type ConfirmTone } from '../ui/ConfirmDialog';

export interface LaunchWizardStep {
  /** Stable id (used as React key). */
  id: string;
  /** Tab label, e.g. "Set up" / "Employees" / "Send". */
  label: string;
  /** Active-step body content. */
  content: React.ReactNode;
  /**
   * Returns true when the user is allowed to move forward from this
   * step. The wizard disables Next / Submit otherwise. Optional —
   * defaults to true.
   */
  isValid?: () => boolean;
  /**
   * Optional inline message rendered below the content when isValid
   * returns false. Helps HR understand what's stopping them.
   */
  invalidHint?: string;
}

export interface LaunchWizardProps {
  /** Tiny uppercase label above the title (e.g. "HR WORKFLOW"). */
  eyebrow: string;
  title: string;
  /** Single-sentence friendly explanation under the title. */
  intro: React.ReactNode;
  /** Top-right back link target. Use `routes.*` builders. */
  backHref: string;
  backLabel?: string;
  /** Three or more step definitions, in order. */
  steps: LaunchWizardStep[];
  /**
   * Called when the user clicks Submit on the LAST step. Should perform
   * the actual mutation (issue tokens, write docs, etc.) and then call
   * onCompleted with the success-view node when done.
   */
  onSubmit: () => Promise<void>;
  /**
   * Optional override label for the submit button. Defaults to "Send".
   */
  submitLabel?: string;
  /** Whether the submit is in-flight. Renders a loading state. */
  submitting?: boolean;
  /**
   * After the submit completes successfully, the wizard advances to a
   * read-only "done" view rendering this node. Passing a non-null value
   * also auto-advances; passing null keeps the user on the last step.
   */
  successView?: React.ReactNode;
  /**
   * When provided, clicking the submit button on the last step shows a
   * confirmation modal BEFORE firing `onSubmit`. The modal lists side-
   * effects (creating DB docs, sending emails, etc.) so HR doesn't
   * accidentally fire an irreversible action.
   *
   * Recommended for any launcher that emails real recipients — without
   * this gate, a single click writes Firestore docs AND mails users,
   * which is too consequential to be one click away.
   */
  confirmBeforeSubmit?: {
    title:         string;
    body?:         string;
    actions?:      ConfirmAction[];
    tone?:         ConfirmTone;
    confirmLabel?: string;
  };
}

const LaunchWizard: React.FC<LaunchWizardProps> = ({
  eyebrow, title, intro, backHref, backLabel = 'Back',
  steps, onSubmit, submitLabel = 'Send', submitting, successView,
  confirmBeforeSubmit,
}) => {
  const [active, setActive] = useState(0);
  const [done, setDone]     = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Append a synthetic "Sent" tab to mirror the legacy 3-tab look even
  // when the launcher only has 2 input steps. We use steps.length + 1
  // as the implicit Sent index. successView controls when it renders.
  const totalTabs = steps.length + 1;
  const isLastInputStep = active === steps.length - 1;
  const onSentTab = done && successView != null;

  const current = steps[active];
  const canForward = current?.isValid ? current.isValid() : true;

  // Do the actual submit. Separate from handleNext so the confirmation
  // dialog can invoke it without re-checking step-position.
  const fireSubmit = async () => {
    setConfirmOpen(false);
    await onSubmit();
  };

  const handleNext = async () => {
    if (isLastInputStep) {
      // High-impact gate: if the parent provided confirmBeforeSubmit,
      // show the modal first. Confirm fires fireSubmit; Cancel returns
      // the user to the last step untouched.
      if (confirmBeforeSubmit) {
        setConfirmOpen(true);
        return;
      }
      await fireSubmit();
    } else {
      setActive(a => Math.min(steps.length - 1, a + 1));
    }
  };

  // Once submit succeeds (signalled by the parent passing a non-null
  // successView), advance to the Sent tab. Effect-driven so the parent
  // can produce successView asynchronously after onSubmit resolves —
  // we don't have to rely on `successView != null` being true at the
  // moment `await onSubmit()` returns.
  React.useEffect(() => {
    if (!done && successView != null && !submitting) {
      setDone(true);
    }
  }, [successView, submitting, done]);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-stone-500">
            {eyebrow}
          </div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">{title}</h1>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">{intro}</p>
        </div>
        <Link to={backHref} className="text-sm text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
          <span className="material-symbols-rounded" style={{ fontSize: 16 }}>arrow_back</span>
          {backLabel}
        </Link>
      </header>

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-3">
        {steps.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            onClick={() => { if (!done) setActive(idx); }}
            disabled={done}
            className={[
              'flex items-center gap-2 rounded-xl border px-4 py-3 text-sm transition text-left',
              active === idx && !onSentTab
                ? 'bg-stone-900 text-white border-stone-900'
                : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400',
              done ? 'opacity-60 cursor-default' : '',
            ].join(' ')}
          >
            <span className={[
              'inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold flex-shrink-0',
              active === idx && !onSentTab
                ? 'bg-white text-stone-900'
                : 'bg-stone-100 text-stone-600',
            ].join(' ')}>
              {idx + 1}
            </span>
            <span className="truncate">{s.label}</span>
          </button>
        ))}
        {/* Synthetic "Sent" pill — only fills the 3rd tab slot when we
            have exactly 2 input steps; for 3+ input steps the tabs
            already fill the row. */}
        {totalTabs === 3 && steps.length === 2 && (
          <div
            className={[
              'flex items-center gap-2 rounded-xl border px-4 py-3 text-sm text-left',
              onSentTab
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-stone-400 border-stone-200',
            ].join(' ')}
          >
            <span className={[
              'inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold flex-shrink-0',
              onSentTab ? 'bg-white text-emerald-700' : 'bg-stone-100 text-stone-400',
            ].join(' ')}>
              3
            </span>
            <span>Sent</span>
          </div>
        )}
      </div>

      {/* Active body */}
      <section className="rounded-xl border border-stone-200 bg-white p-5">
        {onSentTab ? successView : (
          <>
            {current.content}
            {current.isValid && !canForward && current.invalidHint && (
              <div className="mt-3 text-xs text-stone-500">{current.invalidHint}</div>
            )}
          </>
        )}
      </section>

      {/* Footer */}
      {!onSentTab && (
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="secondary"
            icon="arrow_back"
            disabled={active === 0 || submitting}
            onClick={() => setActive(a => Math.max(0, a - 1))}
          >
            Back
          </Button>
          <Button
            variant="submit"
            icon={isLastInputStep ? 'send' : 'arrow_forward'}
            disabled={!canForward || submitting}
            loading={submitting}
            onClick={handleNext}
          >
            {isLastInputStep ? submitLabel : `Next: ${steps[active + 1]?.label ?? ''}`}
          </Button>
        </div>
      )}

      {/* Confirmation modal — only mounted when the parent opted in. */}
      {confirmBeforeSubmit && (
        <ConfirmDialog
          open={confirmOpen}
          title={confirmBeforeSubmit.title}
          body={confirmBeforeSubmit.body}
          actions={confirmBeforeSubmit.actions}
          tone={confirmBeforeSubmit.tone ?? 'warning'}
          confirmLabel={confirmBeforeSubmit.confirmLabel ?? submitLabel}
          loading={submitting}
          onCancel={() => { if (!submitting) setConfirmOpen(false); }}
          onConfirm={fireSubmit}
        />
      )}
    </div>
  );
};

export default LaunchWizard;
